import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import api from '../services/api';
import PDFModalMission from '../components/PDFModalMission';

const MissionOrdersPage = () => {
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theUser, setTheUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [openRefuseModal, setOpenRefuseModal] = useState(false);
  const [selectedIdToRefuse, setSelectedIdToRefuse] = useState(null);
  const [refuseReason, setRefuseReason] = useState('');
  const [openReportModal, setOpenReportModal] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);
  const [reportText, setReportText] = useState('');
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [selectedMissionForPDF, setSelectedMissionForPDF] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [editForm, setEditForm] = useState({
    employee_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    transport_id: '',
    destinations: [],
    billet: ''
  });
  const [employees, setEmployees] = useState([]);
  const [transports, setTransports] = useState([]);

  const filterOptions = [
    { value: 'all', label: 'Toutes les missions' },
    { value: 'validated', label: 'Validées' },
    { value: 'refused', label: 'Refusées' },
    { value: 'pending', label: 'En attente' }
  ];

  useEffect(() => {
    fetchMissions();
    fetchCurrentUser();
    fetchEmployees();
    fetchTransports();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTransports = async () => {
    try {
      const response = await api.get('/transports');
      // Trier les transports par type, puis par marque et modèle
      const sortedTransports = (response.data || []).sort((a, b) => {
        const typeA = a.type?.toLowerCase() || '';
        const typeB = b.type?.toLowerCase() || '';
        if (typeA !== typeB) {
          return typeA.localeCompare(typeB);
        }
        // Si même type, trier par marque puis modèle
        const nameA = `${a.brand || ''} ${a.model || ''}`.toLowerCase();
        const nameB = `${b.brand || ''} ${b.model || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setTransports(sortedTransports);
    } catch (error) {
      console.error('Error fetching transports:', error);
    }
  };

  const fetchMissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mission-orders');
      setMissions(response.data);
    } catch (error) {
      console.error('Error fetching missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const user = await api.get('/auth/me');
      setTheUser(user.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const handleRowClick = async (params) => {
    try {
      const response = await api.get(`/mission-orders/${params.row.id}`);
      setSelectedDetails(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    }
  };

  const handleValidateMission = async (id) => {
    try {
      await api.post('/mission-orders/validate', { id });
      fetchMissions();
    } catch (error) {
      console.error('Error validating mission:', error);
    }
  };

  const handleRefuseMission = async () => {
    try {
      await api.post('/mission-orders/refuse', { id: selectedIdToRefuse, refuseReason });
      setOpenRefuseModal(false);
      setRefuseReason('');
      fetchMissions();
    } catch (error) {
      console.error('Error refusing mission:', error);
    }
  };

  const handleAddReport = async () => {
    try {
      await api.patch(`/mission-orders/${selectedMission.id}/report`, { rapport: reportText });
      setOpenReportModal(false);
      setReportText('');
      setSelectedMission(null);
      fetchMissions();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du rapport:', error);
    }
  };

  const handleDownloadPDF = async (mission) => {
    try {
      // Fetch complete mission data with employee details
      const response = await api.get(`/mission-orders/${mission.id}`);
      setSelectedMissionForPDF(response.data);
      setOpenPDFModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement du PDF:', error);
    }
  };

  const handleDeleteMission = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette mission ?')) {
      try {
        await api.delete(`/mission-orders/${id}`);
        fetchMissions();
      } catch (error) {
        console.error('Error deleting mission:', error);
      }
    }
  };

  const onEditOpen = (mission) => {
    setEditingMission(mission);
    setEditForm({
      employee_id: mission.employee_id || '',
      start_date: mission.validity_from ? new Date(mission.validity_from).toISOString().split('T')[0] : '',
      end_date: mission.validity_to ? new Date(mission.validity_to).toISOString().split('T')[0] : '',
      reason: mission.reason || '',
      transport_id: mission.transport_id || '',
      destinations: mission.destinations || [],
      billet: mission.billet || ''
    });
    setEditOpen(true);
  };

  const onEditClose = () => {
    setEditOpen(false);
    setEditingMission(null);
    setEditForm({
      employee_id: '',
      start_date: '',
      end_date: '',
      reason: '',
      transport_id: '',
      destinations: [],
      billet: ''
    });
  };

  const onEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const onEditSubmit = async () => {
    if (!editingMission) return;
    try {
      // Ne pas envoyer les destinations pour éviter les erreurs de validation
      const { destinations, ...dataToSend } = editForm;
      // Mapper les noms de champs pour le backend
      const backendData = {
        employee_id: dataToSend.employee_id,
        validity_from: dataToSend.start_date,
        validity_to: dataToSend.end_date,
        reason: dataToSend.reason,
        transport_id: dataToSend.transport_id,
        billet: dataToSend.billet
      };
      
      await api.put(`/mission-orders/${editingMission.id}`, backendData);
      onEditClose();
      fetchMissions();
    } catch (error) {
      console.error('Error updating mission:', error);
      alert('Erreur lors de la modification de la mission');
    }
  };

  const getStatusChip = (mission) => {
    if (mission.refused) {
      return <Chip label="Refusé" color="error" size="small" />;
    } else if (mission.validated) {
      return <Chip label="Validé" color="success" size="small" />;
    } else {
      return <Chip label="En attente" color="warning" size="small" />;
    }
  };

  const filteredMissions = missions.filter(mission => {
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'validated' && mission.validated) ||
      (filterStatus === 'refused' && mission.refused) ||
      (filterStatus === 'pending' && !mission.validated && !mission.refused);
    
    const dateFromMatch = !dateFrom || new Date(mission.validity_from) >= new Date(dateFrom);
    const dateToMatch = !dateTo || new Date(mission.validity_to) <= new Date(dateTo);
    
    const employeeMatch = !filterEmployee || 
      mission.employee && `${mission.employee.first_name} ${mission.employee.last_name}`.toLowerCase().includes(filterEmployee.toLowerCase());
    
    return statusMatch && dateFromMatch && dateToMatch && employeeMatch;
  });

  const columns = [
    { field: 'order_number', headerName: 'N° Ordre', width: 120, headerClassName: 'super-app-theme--header' },
    { 
      field: 'employee', 
      headerName: 'Employé', 
      width: 180,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const employee = params.row.employee;
        if (!employee) return '—';
        return `${employee.first_name} ${employee.last_name}`;
      }
    },
    { 
      field: 'destinations', 
      headerName: 'Destination', 
      flex: 1,
      minWidth: 200,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const destinations = params.row.destinations || [];
        if (destinations.length === 0) return '—';
        return destinations.map(d => {
          const wilayaName = d.wilaya?.name || '';
          const dairaName = d.daira || ''; // daira est maintenant un champ libre
          return wilayaName && dairaName ? `${wilayaName}-${dairaName}` : wilayaName || dairaName || '—';
        }).filter(dest => dest !== '—').join(', ');
      }
    },
    { field: 'validity_from', headerName: 'Date Début', width: 130, headerClassName: 'super-app-theme--header' },
    { field: 'validity_to', headerName: 'Date Fin', width: 130, headerClassName: 'super-app-theme--header' },
    { field: 'reason', headerName: 'Objet', flex: 1, minWidth: 150, headerClassName: 'super-app-theme--header' },
    { field: 'refuseReason', headerName: 'Motif de refus', width: 180, headerClassName: 'super-app-theme--header', renderCell: (params) => params.row.refuseReason || '—' },
    {
      field: 'status',
      headerName: 'Statut',
      width: 120,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => getStatusChip(params.row)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1, flexWrap: 'wrap' }}>
            {!params.row.validated && !params.row.refused && (theUser?.role === 1 || theUser?.role === 5 || theUser?.role === 'admin-tenes') && (
              <>
                <Tooltip title="Valider">
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation(); // Empêche la propagation du clic
                    handleValidateMission(params.row.id);
                  }}>
                    <CheckCircleIcon color="success" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refuser">
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation(); // Empêche la propagation du clic
                    setSelectedIdToRefuse(params.row.id);
                    setOpenRefuseModal(true);
                  }}>
                    <CancelIcon color="error" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {params.row.validated && !params.row.rapport && (
              <Tooltip title="Ajouter un rapport">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  setSelectedMission(params.row);
                  setOpenReportModal(true);
                }}>
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>
            )}
            {params.row.validated && (
              <Tooltip title="Télécharger PDF">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDownloadPDF(params.row);
                }}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
            )}
            {(theUser?.role === 1 || theUser?.role === 5 || theUser?.role === 'admin-tenes') && (
              <Tooltip title="Supprimer">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDeleteMission(params.row.id);
                }}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
            {(theUser?.role === 1 || theUser?.role === 5 || theUser?.role === 'admin-tenes') && (
              <Tooltip title="Modifier">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  onEditOpen(params.row);
                }}>
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Ordres de Mission
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/create-mission')}>
          Nouvelle Mission
        </Button>
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={filterStatus}
            label="Statut"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {filterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          size="small"
          label="Employé"
          value={filterEmployee}
          onChange={(e) => setFilterEmployee(e.target.value)}
          placeholder="Filtrer par employé..."
        />
        
        <TextField
          label="Date de début"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        
        <TextField
          label="Date de fin"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Box sx={{ height: 600, width: '100%', mt: 2 }}>
        <DataGrid
          rows={filteredMissions}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          getRowId={(row) => row.id}
          getRowHeight={() => 80}
          onRowClick={handleRowClick}
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: '#1976d2',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-root': {
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1976d2',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderBottom: '2px solid #1565c0',
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus': {
                outline: 'none',
              },
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              padding: '16px 8px',
              borderBottom: '1px solid #f0f0f0',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': {
                backgroundColor: '#fafafa',
              },
              '&:hover': {
                backgroundColor: '#e3f2fd',
                cursor: 'pointer'
              },
              '&.Mui-selected': {
                backgroundColor: '#e3f2fd',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-pagination': {
              '& .MuiIconButton-root': {
                borderRadius: '4px',
              },
            },
          }}
        />
      </Box>

      {/* Refuse Modal */}
      <Dialog open={openRefuseModal} onClose={() => setOpenRefuseModal(false)}>
        <DialogTitle>Refuser la mission</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Motif de Refus"
            fullWidth
            multiline
            rows={4}
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRefuseModal(false)}>Annuler</Button>
          <Button onClick={handleRefuseMission} color="error">Refuser</Button>
        </DialogActions>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={openReportModal} onClose={() => setOpenReportModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ajouter un rapport de mission</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rapport de mission"
            fullWidth
            multiline
            rows={6}
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            placeholder="Décrivez le déroulement de la mission..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportModal(false)}>Annuler</Button>
          <Button onClick={handleAddReport} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Mission Dialog */}
      <Dialog open={editOpen} onClose={onEditClose} maxWidth="lg" fullWidth>
        <DialogTitle>Modifier la mission</DialogTitle>
        <DialogContent>
          {/* Employee Selector */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Employé</InputLabel>
            <Select
              label="Employé"
              name="employee_id"
              value={editForm.employee_id}
              onChange={onEditChange}
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {`${emp.first_name} ${emp.last_name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Dates */}
          <TextField
            margin="dense"
            label="Date de début"
            type="date"
            name="start_date"
            value={editForm.start_date}
            onChange={onEditChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />
          <TextField
            margin="dense"
            label="Date de fin"
            type="date"
            name="end_date"
            value={editForm.end_date}
            onChange={onEditChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: editForm.start_date || new Date().toISOString().split('T')[0] }}
          />

          {/* Reason */}
          <TextField
            margin="dense"
            label="Objet de la mission"
            name="reason"
            value={editForm.reason}
            onChange={onEditChange}
            fullWidth
            multiline
            rows={3}
          />

          {/* Transport */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Moyen de Transport</InputLabel>
            <Select
              label="Moyen de Transport"
              name="transport_id"
              value={editForm.transport_id}
              onChange={onEditChange}
            >
              {transports.map((transport) => (
                <MenuItem key={transport.id} value={transport.id}>
                  {transport.type === 'personnel' 
                    ? `${transport.brand} ${transport.model} (${transport.registration})`
                    : transport.type
                  }
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Billet for special transport types */}
          {transports.find(t => t.id === editForm.transport_id)?.type &&
            ['AVION', 'BUS', 'BATEAU', 'TRAIN'].includes(
              transports.find(t => t.id === editForm.transport_id)?.type?.toUpperCase()
            ) && (
              <TextField
                margin="dense"
                label="Billet"
                name="billet"
                value={editForm.billet || ''}
                onChange={onEditChange}
                fullWidth
              />
            )}

          {/* Destinations */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Destinations ({editForm.destinations.length})
          </Typography>
          {editForm.destinations.map((destination, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Destination {index + 1}</Typography>
              <Typography variant="body2" color="textSecondary">
                {destination.wilaya?.name || 'Wilaya non spécifiée'} - {destination.daira || 'Daïra non spécifiée'}
              </Typography>
            </Box>
          ))}
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
            Note: La modification des destinations nécessite de recréer la mission
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onEditClose}>Annuler</Button>
          <Button onClick={onEditSubmit} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails de l'ordre de mission</DialogTitle>
        <DialogContent>
          {selectedDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
              <div><strong>N° Ordre:</strong> {selectedDetails.order_number}</div>
              <div><strong>Employé:</strong> {selectedDetails.employee?.first_name} {selectedDetails.employee?.last_name}</div>
              <div><strong>Objet:</strong> {selectedDetails.reason}</div>
              <div><strong>Date de début:</strong> {new Date(selectedDetails.validity_from).toLocaleDateString()}</div>
              <div><strong>Date de fin:</strong> {new Date(selectedDetails.validity_to).toLocaleDateString()}</div>
              <div><strong>Destinations:</strong> {
                selectedDetails.destinations?.map(d => {
                  const wilayaName = d.wilaya?.name || '';
                  const dairaName = d.daira || '';
                  return wilayaName && dairaName ? `${wilayaName}-${dairaName}` : wilayaName || dairaName || '—';
                }).filter(dest => dest !== '—').join(', ') || '—'
              }</div>
              <div><strong>Moyen de transport:</strong> {
                selectedDetails.transport ? 
                (selectedDetails.transport.type === 'personnel' 
                  ? `${selectedDetails.transport.brand} ${selectedDetails.transport.model} (${selectedDetails.transport.registration})`
                  : selectedDetails.transport.type
                ) : '—'
              }</div>
              {selectedDetails.billet && (
                <div><strong>Billet:</strong> {selectedDetails.billet}</div>
              )}
              <div><strong>Statut:</strong> 
                {selectedDetails.refused ? 
                  <span style={{ color: 'red', marginLeft: '8px' }}>✗ Refusé</span> : 
                  selectedDetails.validated ? 
                  <span style={{ color: 'green', marginLeft: '8px' }}>✓ Validé</span> :
                  <span style={{ color: 'orange', marginLeft: '8px' }}>⏳ En attente</span>
                }
              </div>
              {selectedDetails.refuseReason && (
                <div><strong>Motif de refus:</strong> {selectedDetails.refuseReason}</div>
              )}
              {selectedDetails.rapport && (
                <div><strong>Rapport:</strong> {selectedDetails.rapport}</div>
              )}
              <div><strong>Créé le:</strong> {new Date(selectedDetails.createdAt).toLocaleDateString()} à {new Date(selectedDetails.createdAt).toLocaleTimeString()}</div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Modal */}
      {openPDFModal && (
        <PDFModalMission
          missionData={selectedMissionForPDF}
          onClose={() => setOpenPDFModal(false)}
        />
      )}
    </Box>
  );
};

export default MissionOrdersPage;
